"use client";

import { useCallback } from "react";
import { toast } from "sonner";

import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";
import techficientService, {
  type TechficientCaseRow,
} from "@/app/_module/services/techficientService";

/**
 * Shared Resume flow for an eApp case — identical behavior to the
 * Applications & Cases list on /quote-enroll, callable from any surface
 * (lead details, dashboards). Handles the popup-vs-iframe dance:
 *
 *  - Fresh URL is ALWAYS fetched (the stored one is the stale
 *    initiate-time link; carrier-stage cases resume on the carrier site).
 *  - Popup-predicted cases pre-reserve the window synchronously inside the
 *    click gesture (popup blockers kill post-await window.open).
 *  - Prediction miss → one-click toast opener instead of a silent failure.
 *  - Popup cases keep the Techficient case view embedded behind the popup.
 */
export function useResumeEappCase() {
  const floater = useEnrollmentFloater();

  const resumeCase = useCallback(
    async (c: TechficientCaseRow) => {
      // This case's carrier window is still open (behind other windows,
      // minimized…) → bring it forward. Fetching a fresh resume URL and
      // re-navigating would restart the carrier's session from page one.
      if (
        floater.popupOpen &&
        floater.popupSession?.caseId === c.techficientCaseId
      ) {
        floater.focusPopup();
        return;
      }
      // Submitted / terminal cases can't be reopened — say so plainly
      // instead of firing a request that comes back as a carrier 400.
      if (c.resumable === false) {
        toast("Application already submitted", {
          description:
            "This one's been sent to the carrier and can no longer be reopened.",
        });
        return;
      }
      const expectPopup = c.launchMode === "popup";
      if (expectPopup && !floater.openPendingPopup()) {
        toast.error("Popup blocked", {
          description:
            "Your browser blocked the application window. Allow popups for this site, then click Resume again.",
        });
        return;
      }
      try {
        const fresh = await techficientService.resumeEapp(c.techficientCaseId);
        const session = {
          caseId: c.techficientCaseId,
          launchUrl: fresh.launchUrl,
          carrier: fresh.carrier || c.carrierName,
          product: fresh.product || c.productName,
        };
        if (fresh.launchMode === "popup") {
          if (expectPopup) {
            floater.completePendingPopup({ ...session, launchMode: "popup" });
          } else {
            const ok = floater.launch({ ...session, launchMode: "popup" });
            if (!ok) {
              toast("Signature step is ready", {
                description: `${session.carrier || "The carrier"} needs to open in its own window.`,
                action: {
                  label: "Open",
                  onClick: () =>
                    floater.launch({ ...session, launchMode: "popup" }),
                },
                duration: 15000,
              });
              return;
            }
          }
          // Keep the Techficient case view embedded behind the popup —
          // the two surfaces are independent.
          if (c.launchUrl) {
            floater.launch({
              caseId: c.techficientCaseId,
              launchUrl: c.launchUrl,
              carrier: c.carrierName,
              product: c.productName,
            });
          }
        } else {
          if (expectPopup) floater.cancelPendingPopup();
          floater.launch(session);
        }
      } catch (e: any) {
        if (expectPopup) floater.cancelPendingPopup();
        // Backend maps an un-resumable case to 409 CASE_NOT_RESUMABLE —
        // show the friendly "already submitted" note, not a scary error.
        const status = e?.response?.status ?? e?.status;
        const code = e?.response?.data?.code ?? e?.code;
        if (status === 409 || code === "CASE_NOT_RESUMABLE") {
          toast("Application already submitted", {
            description:
              "This one's been sent to the carrier and can no longer be reopened.",
          });
          return;
        }
        toast.error("Couldn't resume application", {
          description:
            e?.message ||
            "Fetching a fresh application link failed — try again.",
        });
      }
    },
    [floater],
  );

  // Open a case's stored application in the iframe for READ-ONLY viewing.
  // Used for submitted / abandoned / terminal cases: /app/resume 400s on
  // those, but the initiate-time launchUrl still loads their status +
  // "Open Carrier App" / completion screen. techf.com → embed; carrier
  // finish URLs → popup (their own window).
  const viewCase = useCallback(
    (c: TechficientCaseRow) => {
      if (!c.launchUrl) return;
      if (
        floater.popupOpen &&
        floater.popupSession?.caseId === c.techficientCaseId
      ) {
        floater.focusPopup();
        return;
      }
      let host = "";
      try {
        host = new URL(c.launchUrl).hostname;
      } catch {
        /* ignore */
      }
      floater.launch({
        caseId: c.techficientCaseId,
        launchUrl: c.launchUrl,
        carrier: c.carrierName,
        product: c.productName,
        // Stored techf URL → iframe; anything else → popup. Exact-host
        // match ($) so techf.com.evil.com can't slip into the iframe.
        launchMode:
          host === "techf.com" || host.endsWith(".techf.com")
            ? "iframe"
            : "popup",
      });
    },
    [floater],
  );

  return { resumeCase, viewCase, floater };
}

export default useResumeEappCase;
